<script lang="ts" setup>
import {
  PieChart01,
  TypeSquare,
  Phone,
  Microphone01,
  Image03,
  PieChart02,
  Trash01,
  Edit02,
  Clock,
  PhoneHangUp,
} from "utruckin-icons";

import CustomPanel from "@/components/custom/CustomPanel.vue";
import {useStatsStore} from "@/features/stats/stats.store";
import {ICountFilter, IFilter} from "@/types/main";
import {computed, ref, watch} from "vue";
import {useAppStore} from "@/store/app.store";
import {useRoute} from "vue-router";

interface IProps {
  type: 'messages' | 'chats' | 'groups';
  position: "horizontal" | "vertical";
  filter: IFilter;
  store: any;
}

const store = useStatsStore();
const appStore = useAppStore();
const route = useRoute();

const loading = ref<boolean>(false);
const data = ref()


const props = withDefaults(defineProps<IProps>(), {
  position: "horizontal",
});

const countFilter = computed(() => {
  if (props.filter) {
    return {
      account_id: props.filter.account,
      time_from: props.filter.time_from,
      time_to: props.filter.time_to,
      date_from: props.filter.date_from,
      date_to: props.filter.date_to,
      shift_id: props.filter.shift,
    }
  }
})

const getInfo = async () => {
  data.value = await store.getStats(countFilter.value as ICountFilter, loading);
}

const info = computed(() => {
  const isMessages = props.type === 'messages';
  const isChatsUser = props.type === 'chats';

  const source = isMessages
      ? data.value
      : isChatsUser
          ? data.value?.chats?.user
          : data.value?.chats?.group;

  return {
    messages: {
      total: source?.messages?.total ?? 0,
      text: source?.messages?.text ?? 0,
      calls: source?.messages?.calls ?? 0,
      voice:
          (source?.messages?.voice ?? 0) +
          (source?.messages?.document ?? 0) +
          (source?.messages?.audio ?? 0),
      photo_video:
          (source?.messages?.photo ?? 0) +
          (source?.messages?.video ?? 0) +
          (source?.messages?.round ?? 0),
    },
    errors: {
      total: source?.errors?.total ?? 0,
      reply_time: source?.errors?.reply_time_exceed ?? 0,
      deleted: source?.errors?.deleted ?? 0,
      edited: source?.errors?.edited ?? 0,
      unanswered:
          (source?.errors?.declined_calls ?? 0) +
          (source?.errors?.missed_calls ?? 0),
    }
  };
});

const filterPanels = () => {
  if (store.panels.allMessages) {
    for (let panelsKey in props.store.additionalFilter) {
      props.store.additionalFilter[panelsKey] = null;
    }

    return
  } else {
    if (store.panels.allErrors) {
      props.store.additionalFilter.is_deleted = true
      props.store.additionalFilter.is_edited = true
      props.store.additionalFilter.missed_call = true
      props.store.additionalFilter.declined_call = true
      props.store.additionalFilter.reply_time_exceed = true

      props.store.additionalFilter.messages_text = false
      props.store.additionalFilter.messages_photo = false
      props.store.additionalFilter.messages_video = false
      props.store.additionalFilter.messages_voice = false
      props.store.additionalFilter.messages_round = false
      props.store.additionalFilter.messages_audio = false
      props.store.additionalFilter.messages_document = false

      return
    } else {

      for (let panelsKey in props.store.additionalFilter) {
        props.store.additionalFilter[panelsKey] = false;
      }

      if (store.panels.text) {
        props.store.additionalFilter.messages_text = true
      }

      if (store.panels.call) {
        props.store.additionalFilter.calls = true
      }

      if (store.panels.voice) {
        props.store.additionalFilter.messages_voice = true
        props.store.additionalFilter.messages_document = true
        props.store.additionalFilter.messages_audio = true
      }

      if (store.panels.photo) {
        props.store.additionalFilter.messages_photo = true
        props.store.additionalFilter.messages_video = true
        props.store.additionalFilter.messages_round = true
      }

      if (store.panels.replyTime) {
        props.store.additionalFilter.reply_time_exceed = true
      }

      if (store.panels.unanswered) {
        props.store.additionalFilter.missed_call = true
        props.store.additionalFilter.declined_call = true
      }

      if (store.panels.deleted) {
        props.store.additionalFilter.is_deleted = true
      }

      if (store.panels.edited) {
        props.store.additionalFilter.is_edited = true
      }
    }
  }
}

watch(
    [
      () => appStore.refreshKey,
      () => JSON.stringify(countFilter.value),
    ],
    () => {
      getInfo()
    },
    {immediate: true}
)

watch(
    () => JSON.stringify(store.panels),
    () => {
      if (props.type == 'messages') {
        filterPanels()
      }
    }
)

watch(
    () => route.path,
    () => {
      store.setDefaultPanels()
    },
    {immediate: true}
)

</script>

<template>
  <div class="c-w-panels" :class="position">
    <div class="column">
      <CustomPanel
          :icon="PieChart01"
          :number="info.messages.total"
          size="large"
          label="All Message"
          @click="store.selectPanel('allMessages')"
          :is-active="store.panels.allMessages"
      />
    </div>

    <div class="column">
      <CustomPanel
          :number="info.messages.text"
          label="Text"
          @click="store.selectPanel('text')"
          :is-active="store.panels.text"
      />
      <CustomPanel
          :number="info.messages.calls"
          label="Call"
          @click="store.selectPanel('call')"
          :is-active="store.panels.call"
      />
      <CustomPanel
          :number="info.messages.voice"
          label="Voice & Document"
          @click="store.selectPanel('voice')"
          :is-active="store.panels.voice"
      />
      <CustomPanel
          :number="info.messages.photo_video"
          label="Photo & Video"
          @click="store.selectPanel('photo')"
          :is-active="store.panels.photo"
      />
    </div>
    <div class="column">
      <CustomPanel
          :icon="PieChart02"
          :number="info.errors.total"
          type="error"
          size="large"
          label="All errors"
          @click="store.selectPanel('allErrors')"
          :is-active="store.panels.allErrors"
      />
    </div>
    <div class="column">
      <CustomPanel
          :number="info.errors.reply_time"
          type="error"
          label="Reply time"
          @click="store.selectPanel('replyTime')"
          :is-active="store.panels.replyTime"
      />
      <CustomPanel
          :number="info.errors.unanswered"
          type="error"
          label="Unanswered"
          @click="store.selectPanel('unanswered')"
          :is-active="store.panels.unanswered"
      />
      <CustomPanel
          :number="info.errors.deleted"
          type="error"
          label="Deleted"
          @click="store.selectPanel('deleted')"
          :is-active="store.panels.deleted"
      />
      <CustomPanel
          :number="info.errors.edited"
          type="error"
          label="Edited"
          @click="store.selectPanel('edited')"
          :is-active="store.panels.edited"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.c-w-panels {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 16px;
  border-bottom: 1px solid var(--color-border);

  &.vertical {
    flex-direction: column;
    border-bottom: none;
    padding: 0;

    .column {
      flex-direction: column;
    }
  }

  .column {
    display: flex;
    gap: 8px;

    &:nth-child(1),
    &:nth-child(3) {
      flex: 1 1 0;
    }

    &:nth-child(2),
    &:nth-child(4) {
      flex: 2 1 0;
    }
  }
}
</style>
